import { defineComponent, ref, computed, watch, nextTick, onMounted, createBlock, openBlock, Teleport, createVNode, Transition, withCtx, withDirectives, createElementVNode, withModifiers, toDisplayString, createCommentVNode, createElementBlock, Fragment, renderList, mergeProps, vShow } from 'vue';
import { useFocusTrap } from './basler-components.es407.js';
import { useKeenSlider as z } from './basler-components.es387.js';
import _sfc_main$2 from './basler-components.es110.js';
/* empty css                        */
import _sfc_main$1 from './basler-components.es72.js';
import { useScrollbarWidth } from './basler-components.es408.js';

const _hoisted_1 = ["aria-hidden"];
const _hoisted_2 = { class: "visually-hidden" };
const _hoisted_3 = { class: "lightbox-gallery__main" };
const _hoisted_4 = { class: "lightbox-gallery__slide-content" };
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "LightboxGallery",
  props: {
    title: {},
    mediaList: {},
    isOpen: { type: Boolean, default: false },
    triggerBtnLabel: {},
    nextBtnLabel: { default: "Next media" },
    prevBtnLabel: { default: "Previous media" },
    closeBtnLabel: { default: "Close lightbox" }
  },
  emits: ["next", "prev", "close"],
  setup(__props, { expose: __expose, emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const lightboxRef = ref(null);
    const isOpen = ref(props.isOpen ?? false);
    const currentSlide = ref(0);
    const sliderIsDragged = ref(false);
    const elementToFocusAfterClose = ref(null);
    const showNavButtons = computed(() => {
      return props.mediaList?.length > 1;
    });
    const { activate: activateFocusTrap, deactivate: deactivateFocusTrap } = useFocusTrap(lightboxRef);
    const [slideTrackRef, slider] = z({
      loop: true,
      selector: ".lightbox-gallery__slide",
      initial: currentSlide.value,
      slideChanged: (slider2) => {
        if (!sliderIsDragged.value && isOpen.value) {
          currentSlide.value = slider2.track.details.rel;
        }
      },
      dragEnded: (slider2) => {
        sliderIsDragged.value = false;
        currentSlide.value = slider2.track.details.rel;
      },
      dragged: () => {
        if (!sliderIsDragged.value) {
          sliderIsDragged.value = true;
        }
      },
      slides: {
        origin: "center",
        perView: 1,
        spacing: 11
      },
      breakpoints: {
        "(min-width: 992px)": {
          slides: {
            spacing: 41
          }
        }
      }
    });
    watch(
      isOpen,
      async (isOpen2) => {
        await nextTick();
        if (isOpen2 && lightboxRef.value) {
          activateFocusTrap();
          lightboxRef.value.focus();
          await new Promise((resolve) => {
            setTimeout(() => {
              typeof window !== "undefined" && window.dispatchEvent(new Event("resize"));
              resolve(true);
            }, 100);
          });
          const mediaItem = props.mediaList[currentSlide.value]?.media;
          if (!mediaTypeHasSpecialContent(mediaItem)) {
            slider.value?.moveToIdx(currentSlide.value);
          }
        } else {
          currentSlide.value = 0;
          slider.value?.moveToIdx(0);
          deactivateFocusTrap();
        }
      },
      { immediate: true }
    );
    const mediaTypeHasSpecialContent = (media) => {
      return media?.type === "iframe" || media?.type === "youtube" || media?.type === "animation";
    };
    const nextSlide = () => {
      slider.value?.next();
      emit("next");
    };
    const prevSlide = () => {
      slider.value?.prev();
      emit("prev");
    };
    const openLightbox = (index = 0, event) => {
      currentSlide.value = index > props.mediaList?.length - 1 ? 0 : index;
      isOpen.value = true;
      if (event && event.currentTarget) {
        elementToFocusAfterClose.value = event.currentTarget;
      }
    };
    const closeLightbox = async () => {
      isOpen.value = false;
      currentSlide.value = 0;
      emit("close");
      if (elementToFocusAfterClose.value) {
        await nextTick();
        elementToFocusAfterClose.value.focus();
      }
    };
    const onKeydown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeLightbox();
      } else if (event.key === "ArrowRight") {
        nextSlide();
      } else if (event.key === "ArrowLeft") {
        prevSlide();
      }
    };
    const onSpecialContentLoaded = () => {
      slider.value?.moveToIdx(currentSlide.value, false, { duration: 0 });
    };
    __expose({
      openLightbox,
      closeLightbox
    });
    onMounted(() => {
      useScrollbarWidth();
    });
    return (_ctx, _cache) => {
      return openBlock(), createBlock(Teleport, { to: "body" }, [
        createVNode(Transition, { name: "lightbox-fade" }, {
          default: withCtx(() => [
            withDirectives(createElementVNode("div", {
              ref_key: "lightboxRef",
              ref: lightboxRef,
              class: "lightbox-gallery",
              role: "dialog",
              "aria-modal": "true",
              "aria-hidden": !isOpen.value,
              tabindex: "-1",
              onKeydown,
              onClick: _cache[0] || (_cache[0] = withModifiers(
                //@ts-ignore
                (...args) => _ctx.onClose && _ctx.onClose(...args),
                ["self"]
              ))
            }, [
              createElementVNode("h2", _hoisted_2, toDisplayString(_ctx.title), 1),
              createVNode(_sfc_main$1, {
                "icon-type": "icon-only",
                "icon-name": "close-thick",
                "style-type": "primary",
                class: "lightbox-gallery__btn-close",
                "aria-label": _ctx.closeBtnLabel,
                onClick: closeLightbox
              }, null, 8, ["aria-label"]),
              createElementVNode("div", _hoisted_3, [
                createElementVNode("ul", {
                  ref_key: "slideTrackRef",
                  ref: slideTrackRef,
                  class: "lightbox-gallery__track"
                }, [
                  (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.mediaList, (mediaItem, index) => {
                    return openBlock(), createElementBlock("li", {
                      key: index,
                      class: "lightbox-gallery__slide"
                    }, [
                      createElementVNode("div", _hoisted_4, [
                        currentSlide.value === index ? (openBlock(), createBlock(_sfc_main$2, mergeProps({
                          key: 0,
                          ref_for: true
                        }, mediaItem, {
                          cols: [10, 10, 12],
                          onIframeLoaded: onSpecialContentLoaded,
                          onAnimationLoaded: onSpecialContentLoaded
                        }), null, 16)) : createCommentVNode("", true)
                      ])
                    ]);
                  }), 128))
                ], 512),
                showNavButtons.value ? (openBlock(), createBlock(_sfc_main$1, {
                  key: 0,
                  "icon-type": "icon-only",
                  "icon-name": "arrow-left",
                  "style-type": "primary",
                  class: "lightbox-gallery__prev",
                  "aria-label": _ctx.prevBtnLabel,
                  onClick: prevSlide
                }, null, 8, ["aria-label"])) : createCommentVNode("", true),
                showNavButtons.value ? (openBlock(), createBlock(_sfc_main$1, {
                  key: 1,
                  "icon-type": "icon-only",
                  "icon-name": "arrow-right",
                  "style-type": "primary",
                  class: "lightbox-gallery__next",
                  "aria-label": _ctx.nextBtnLabel,
                  onClick: nextSlide
                }, null, 8, ["aria-label"])) : createCommentVNode("", true)
              ])
            ], 40, _hoisted_1), [
              [vShow, isOpen.value]
            ])
          ]),
          _: 1
        })
      ]);
    };
  }
});

export { _sfc_main as default };
